import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { unionBy } from 'lodash';

import writableScribeDocumentService from '../services/writableScribeDocumentService';

const DEFAULT_ORGS_PER_PAGE = 20;

/**
 * @typedef {Object} ListWritableDocumentsPayload
 * @property {string} [searchQuery]
 * @property {number} [skip]
 * @property {number} [limit]
 */
export const listWritableDocuments = createAsyncThunk(
  'writableScribeDocuments/listWritableDocuments',
  /**
   * @param {ListWritableDocumentsPayload} payload
   * @returns {Promise<Array<import('scribe-web-shared/types/organization').Organization>>}
   */
  async ({ searchQuery, skip, limit } = {}) => {
    const orgs = await writableScribeDocumentService.listWritableDocuments({
      searchQuery,
      skip,
      getFirst: limit || DEFAULT_ORGS_PER_PAGE
    });

    return orgs;
  }
);

const INITIAL_STATE = {
  isLoading: false,
  isError: false,
  documents: [],
  totalDocuments: 0,
  searchQuery: '',
  loadedAll: false
};

const writableScribeDocumentsSlice = createSlice({
  name: 'writableScribeDocuments',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listWritableDocuments.fulfilled, (state, action) => {
        const { scribe_documents, total } = action.payload;
        const query = action.meta?.arg?.query || '';
        const skip = action.meta?.arg?.skip;

        if (query !== state.searchQuery || !skip) {
          state.documents = [...scribe_documents];
        } else {
          state.documents = unionBy(state.documents, scribe_documents, 'id');
        }
        state.totalDocuments = total;
        state.loadedAll = state.documents.length >= total;
        state.isLoading = false;
        state.isError = false;
        state.searchQuery = query;
      })
      .addCase(listWritableDocuments.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(listWritableDocuments.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

export default writableScribeDocumentsSlice.reducer;
